import { Scale, Landmark, Wallet, FileCheck2, Users, Smartphone } from "lucide-react";
import Seo from "@/components/Seo";
import CtaBand from "@/components/CtaBand";
import { FadeIn, Eyebrow, MaskedLine } from "@/components/Reveal";

const BASE_URL = "https://www.contabeleza.com.br";

const SOLUTIONS = [
    {
        icon: Scale,
        id: "lei-do-salao-parceiro",
        tag: "Lei do Salão Parceiro",
        title: "Estruturação da parceria com mais segurança jurídica",
        text: "Contratos de parceria, homologação e orientação sobre a divisão de cotas, com processos estruturados conforme os requisitos da Lei nº 13.352/2016, contribuindo para a redução de riscos trabalhistas.",
    },
    {
        icon: Landmark,
        id: "gestao-tributaria",
        tag: "Gestão tributária",
        title: "O imposto certo para o seu modelo de negócio",
        text: "Enquadramento fiscal revisado, orientação sobre notas discriminadas entre cota-salão e cota-parceiro e apuração transparente. Você entende exatamente o que paga, e por quê.",
    },
    {
        icon: Wallet,
        id: "bpo-financeiro",
        tag: "BPO financeiro",
        title: "Seu financeiro profissional, sem contratar ninguém",
        text: "Contas a pagar e a receber, conciliação, fluxo de caixa e relatórios que você lê em minutos. Nossa equipe cuida dos números enquanto você cuida dos clientes.",
    },
    {
        icon: FileCheck2,
        id: "abertura-e-regularizacao",
        tag: "Abertura e regularização",
        title: "Da informalidade ao CNPJ sem sufoco",
        text: "Abertura de empresa, alvarás, certificados e regularização de pendências. O caminho mais curto e seguro para formalizar o seu negócio da beleza.",
    },
    {
        icon: Users,
        id: "pessoas-e-equipe",
        tag: "Pessoas e equipe",
        title: "Contratações e rotinas trabalhistas em ordem",
        text: "Admissões, folha, pró-labore e obrigações acessórias organizados, para CLTs e para profissionais parceiros, cada um no seu regime correto.",
    },
    {
        icon: Smartphone,
        id: "tecnologia-iloveme",
        tag: "Tecnologia ilove.me",
        title: "Apoio na implantação do sistema de gestão",
        text: "Apoiamos a implantação do ilove.me, agenda, comissões, caixa, estoque e relatórios, alinhando a rotina de gestão ao acompanhamento contábil da Contabeleza.",
    },
];

export default function SolutionsPage() {
    const jsonLd = {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "BreadcrumbList",
                itemListElement: [
                    { "@type": "ListItem", position: 1, name: "Início", item: `${BASE_URL}/` },
                    { "@type": "ListItem", position: 2, name: "Soluções", item: `${BASE_URL}/solucoes` },
                ],
            },
            {
                "@type": "ItemList",
                name: "Soluções Contabeleza para negócios da beleza",
                itemListElement: SOLUTIONS.map((s, i) => ({
                    "@type": "ListItem",
                    position: i + 1,
                    item: {
                        "@type": "Service",
                        name: s.title,
                        description: s.text,
                        serviceType: s.tag,
                        provider: { "@type": "Organization", name: "Contabeleza", url: `${BASE_URL}/` },
                        areaServed: "BR",
                    },
                })),
            },
        ],
    };

    return (
        <div className="page-bg-solucoes">
            <Seo
                title="Soluções, Contabeleza | Lei do Salão Parceiro, tributos, BPO e tecnologia"
                description="Assessoria completa para negócios da beleza: Lei do Salão Parceiro, gestão tributária, BPO financeiro, abertura de empresa, rotinas de equipe e tecnologia ilove.me."
                jsonLd={jsonLd}
            />

            <section className="page-hero-solucoes relative overflow-hidden px-5 pb-20 pt-32 sm:px-8 sm:pt-40 sm:pb-28">
                <div className="relative mx-auto max-w-7xl text-center">
                    <FadeIn y={16}>
                        <Eyebrow color="text-[#FCB21E]">Soluções</Eyebrow>
                    </FadeIn>
                    <h1 className="mx-auto mt-5 max-w-3xl font-display text-4xl leading-[1.08] tracking-tight text-[#F3EDE6] sm:text-5xl lg:text-6xl">
                        <MaskedLine delay={0.1}>Tudo que o seu negócio</MaskedLine>
                        <MaskedLine delay={0.25}>
                            precisa, <span className="text-[#FCB21E]">em um só lugar.</span>
                        </MaskedLine>
                    </h1>
                    <FadeIn delay={0.45} className="mx-auto mt-6 max-w-xl">
                        <p className="text-base leading-relaxed text-white/85 sm:text-lg">
                            Seis frentes que se conectam: jurídico, tributário, financeiro e tecnológico. Você não
                            precisa de cinco fornecedores, precisa de um ecossistema.
                        </p>
                    </FadeIn>
                </div>
            </section>


            <section className="px-5 py-20 sm:px-8 sm:py-28">
                <div className="mx-auto max-w-7xl">
                    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {SOLUTIONS.map((s, i) => (
                            <FadeIn key={s.tag} delay={(i % 3) * 0.1}>
                                <article
                                    id={s.id}
                                    data-testid={`solution-card-${i + 1}`}
                                    className={`group flex h-full scroll-mt-28 flex-col rounded-3xl border p-8 transition-all duration-300 hover:-translate-y-1.5 hover:border-[#0047BB]/25 hover:shadow-[0_24px_60px_rgba(8,16,79,0.12)] ${
                                        i === 0
                                            ? "border-[#0047BB]/20 bg-gradient-to-br from-[#B2D0FF]/25 to-[#FFFDFA] md:col-span-2"
                                            : "border-[#08104F]/8 bg-[#FFFDFA]"
                                    }`}
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#B2D0FF]/30 text-[#0047BB] transition-colors duration-300 group-hover:bg-[#0047BB] group-hover:text-white">
                                            <s.icon className="h-5 w-5" strokeWidth={1.75} />
                                        </span>
                                        <span className="font-display text-4xl text-[#08104F]/10">
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                    </div>
                                    <p className="mt-7 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#0047BB]">
                                        {s.tag}
                                    </p>
                                    <h2 className="mt-3 font-display text-xl leading-snug text-[#08104F]">{s.title}</h2>
                                    <p className="mt-4 text-sm leading-relaxed text-[#3A4768]">{s.text}</p>
                                </article>
                            </FadeIn>
                        ))}
                    </div>
                </div>
            </section>

            <CtaBand
                title="Qual dessas dores é a sua hoje?"
                text="Conte para um especialista no diagnóstico gratuito. Em uma conversa, você descobre o que está travando o seu negócio, e como destravar."
            />
        </div>
    );
}
